/**
 * SignalR Notification Service — Singleton connection manager
 *
 * Matches Xamarin SignalRNofiticationService:
 * - negotiate() → POST /negotiate?code={functionCode} with access_token header
 * - subscribe() → HubConnection to Azure SignalR, listens on "Plant{orgId}" channel
 * - unsubscribe() → stops the hub connection
 * - Auto-reconnect on disconnect (withAutomaticReconnect)
 * - Guard flag prevents concurrent subscribe calls (_isConnecting)
 */

import * as signalR from '@microsoft/signalr';

import { Endpoints, YMS_API_CONFIG } from '@/constants';

import type { SignalRNegotiateResponseDTO, SignalRNotificationPayloadDTO } from '@/types';

// ─────────────────────────────────────────────────────────────────
// Module-level singleton state (matches middlewareClient pattern in taskService.ts)
// ─────────────────────────────────────────────────────────────────

let hub: signalR.HubConnection | null = null;
let isConnecting = false;
let isUnsubscribed = false;

// ─────────────────────────────────────────────────────────────────
// Negotiate — POST to get SignalR hub URL + access token
// Matches Xamarin: _client.Request(Endpoints.SignalRNotificationAPI + functionCode)
//   .WithHeader(AppConstants.AccessToken, accessToken)
//   .PostAsync().ReceiveJson<NotificationResponseModelDTO>()
// ─────────────────────────────────────────────────────────────────

export async function negotiate(
  functionCode: string,
  accessToken: string
): Promise<SignalRNegotiateResponseDTO> {
  // Matches Xamarin exactly: Flurl PostAsync() sends POST with NO body and NO Content-Type.
  // Using fetch instead of ymsAdminClient to avoid the default Content-Type: application/json
  // header that causes 401 on the Azure SignalR negotiate endpoint.
  const url = `${YMS_API_CONFIG.adminBaseUrl}${Endpoints.SignalRNegotiateAPI}${functionCode}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'access-Token': accessToken,
    },
  });

  if (!response.ok) {
    throw new Error(`Negotiate failed: ${String(response.status)}`);
  }

  const data: SignalRNegotiateResponseDTO = (await response.json()) as SignalRNegotiateResponseDTO;
  return data;
}

// ─────────────────────────────────────────────────────────────────
// Subscribe — Connect to SignalR hub and listen on plant channel
// Matches Xamarin SubscribePlantNotification()
// ─────────────────────────────────────────────────────────────────

export interface SignalRCallbacks {
  onNotification: (payload: SignalRNotificationPayloadDTO) => void;
  onReconnected?: () => void;
}

export async function subscribe(
  hubUrl: string,
  hubAccessToken: string,
  orgId: number,
  callbacks: SignalRCallbacks
): Promise<void> {
  // Guard: prevent concurrent connections (matches Xamarin _isConnecting check)
  if (isConnecting) return;
  if (hub && hub.state !== signalR.HubConnectionState.Disconnected) return;

  try {
    isConnecting = true;
    isUnsubscribed = false;

    // Build hub connection — matches Xamarin HubConnectionBuilder
    hub = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => hubAccessToken,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(
        import.meta.env.DEV ? signalR.LogLevel.Information : signalR.LogLevel.Warning
      )
      .build();

    // Channel name: "plant-{orgId}" — matches server-side SignalR group name
    const channelName = `plant-${String(orgId)}`;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[SignalR] Subscribing to channel: "${channelName}"`);
    }
    hub.on(channelName, (message: unknown) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[SignalR] Raw message received:', message);
      }
      try {
        const payload =
          typeof message === 'string'
            ? (JSON.parse(message) as SignalRNotificationPayloadDTO)
            : (message as SignalRNotificationPayloadDTO);
        callbacks.onNotification(payload);
      } catch {
        if (import.meta.env.DEV) {
          console.warn('[SignalR] Failed to parse notification message:', message);
        }
      }
    });

    // Matches Xamarin Hub_Closed — log reconnection events
    hub.onreconnected(() => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[SignalR] Reconnected to hub');
      }
      callbacks.onReconnected?.();
    });

    hub.onclose((error: Error | undefined) => {
      isConnecting = false;
      if (error?.message && import.meta.env.DEV) {
        console.warn('[SignalR] Connection closed with error:', error.message);
      }
    });

    // Start connection — matches Xamarin StartSignalRService()
    await hub.start();
    isConnecting = false;

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[SignalR] Connected and listening on channel: ${channelName}`);
    }
  } catch (error: unknown) {
    isConnecting = false;
    const message = error instanceof Error ? error.message : 'Unknown error';
    if (import.meta.env.DEV) {
      console.error('[SignalR] Failed to connect:', message);
    }
    throw error;
  }
}

// ─────────────────────────────────────────────────────────────────
// Unsubscribe — Stop the hub connection
// Matches Xamarin UnSubscribePlantNotification()
// ─────────────────────────────────────────────────────────────────

export async function unsubscribe(): Promise<void> {
  isUnsubscribed = true;
  if (hub?.state === signalR.HubConnectionState.Connected) {
    try {
      await hub.stop();
    } catch {
      // Silently ignore stop errors during cleanup
    }
  }
  hub = null;
  isConnecting = false;
}

// ─────────────────────────────────────────────────────────────────
// Disconnect — synchronous fire-and-forget for logout
// ─────────────────────────────────────────────────────────────────

export function disconnectSignalR(): void {
  isUnsubscribed = true;
  if (hub) {
    void hub.stop();
    hub = null;
  }
  isConnecting = false;
}

// ─────────────────────────────────────────────────────────────────
// Status check
// ─────────────────────────────────────────────────────────────────

export function isSignalRConnected(): boolean {
  return hub !== null && hub.state === signalR.HubConnectionState.Connected;
}

export function isSignalRUnsubscribed(): boolean {
  return isUnsubscribed;
}

// ─────────────────────────────────────────────────────────────────
// Export facade
// ─────────────────────────────────────────────────────────────────

export const signalRService = {
  negotiate,
  subscribe,
  unsubscribe,
  disconnect: disconnectSignalR,
  isConnected: isSignalRConnected,
} as const;
